import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const supportsHas = typeof CSS !== "undefined" && CSS.supports?.("selector(:has(a))");
/** Info icons across the app carry their text in `title`, which touch screens never show.
 *  Buttons with `aria-expanded` open their own popover, so they are left alone. */
export const INFO_SELECTOR = supportsHas
  ? "[title].cursor-help:not([aria-expanded]), [title]:not([aria-expanded]):has(> svg.lucide-info)"
  : "[title].cursor-help:not([aria-expanded])";

/** Below the icon when it fits, otherwise above; always kept inside the viewport. */
export function placeTip(rect, size, view, gap = 8, margin = 8) {
  const maxLeft = Math.max(margin, view.width - size.width - margin);
  const left = Math.min(Math.max(rect.left + rect.width / 2 - size.width / 2, margin), maxLeft);
  const below = rect.bottom + gap;
  const top =
    below + size.height <= view.height - margin
      ? below
      : Math.max(margin, rect.top - gap - size.height);
  return { left, top };
}

/** Touch devices only: tapping an info icon shows its text in a bubble. Hover devices keep the native tooltip. */
export default function InfoTapTip() {
  const [tip, setTip] = useState(null);
  const [pos, setPos] = useState(null);
  const bubbleRef = useRef(null);

  useEffect(() => {
    const touch = window.matchMedia?.("(hover: none)");
    if (!touch) return undefined;
    const onClick = (e) => {
      if (!touch.matches) return;
      const el = e.target.closest?.(INFO_SELECTOR);
      const text = el && (el.getAttribute("title") || el.getAttribute("aria-label"));
      if (!text) {
        setTip(null);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setTip((cur) => (cur?.el === el ? null : { el, text }));
    };
    const close = () => setTip(null);
    document.addEventListener("click", onClick, true);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, []);

  useLayoutEffect(() => {
    if (!tip || !bubbleRef.current) return;
    const size = bubbleRef.current.getBoundingClientRect();
    setPos(
      placeTip(tip.el.getBoundingClientRect(), size, {
        width: window.innerWidth,
        height: window.innerHeight,
      })
    );
  }, [tip]);

  if (!tip) return null;

  return createPortal(
    <div
      ref={bubbleRef}
      role="tooltip"
      className="fixed z-[13000] max-w-[min(20rem,calc(100vw-1rem))] whitespace-pre-line rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm leading-snug text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-panel)] animate-in fade-in zoom-in-95 duration-150 motion-reduce:animate-none"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
    >
      {tip.text}
    </div>,
    document.body
  );
}
